import { type ApiResponse } from '@type/api'
import { axiosProxy } from '@utils/axios'

import { type RequestApi } from './types'

/**
 * Снимает повтор внутри axiosProxy: сколько раз повторять, решает вызывающий код. У создания,
 * правки, ответа и отказа фулфилмента повторов нет вовсе — второй такой же запрос после сетевого
 * сбоя завёл бы вторую заявку или повторил действие по уже изменившейся версии. Чтению снимка
 * один повтор разрешён в самом запросе.
 */
const skipRetryConfig = { headers: { 'X-Evirma-Skip-Retry': '1' } }

const create = (request: RequestApi.SaveRequest) =>
  axiosProxy.post<ApiResponse<RequestApi.SingleData>>('/fulfillment/request/create', request, skipRetryConfig)

const getRequest = (id: string) =>
  axiosProxy.get<ApiResponse<RequestApi.EditSnapshotData>>(`/fulfillment/request/${id}`, skipRetryConfig)

const edit = (id: string, request: RequestApi.EditRequest) =>
  axiosProxy.post<ApiResponse<RequestApi.EditResultData>>(`/fulfillment/request/${id}/edit`, request, skipRetryConfig)

const myList = () => axiosProxy.get<ApiResponse<RequestApi.ListData>>('/fulfillment/request/my')

const inbox = () => axiosProxy.get<ApiResponse<RequestApi.InboxData>>('/fulfillment/request/inbox')

const inboxCount = () => axiosProxy.get<ApiResponse<RequestApi.InboxCountData>>('/fulfillment/request/inbox/count')

/** Отказ убирает заявку из новых; отмена отказа возвращает её обратно. */
const decline = (id: string, request: RequestApi.DeclineRequest) =>
  axiosProxy.post<ApiResponse<null>>(`/fulfillment/request/${id}/decline`, request, skipRetryConfig)

const cancelDecline = (id: string) =>
  axiosProxy.post<ApiResponse<null>>(`/fulfillment/request/${id}/decline/cancel`, {})

const offer = (id: string, request: RequestApi.OfferRequest) =>
  axiosProxy.post<ApiResponse<RequestApi.SingleData>>(`/fulfillment/request/${id}/offer`, request, skipRetryConfig)

/**
 * Файл уходит в отдельный маршрут фронтенда: общий прокси /api/server работает только с JSON.
 * Заявка к этому моменту уже создана — вложение привязывается к ней.
 */
const attachFile = async (id: string, file: globalThis.File) => {
  const body = new FormData()
  body.append('file', file, file.name)

  const response = await fetch(`/api/fulfillment/request/${id}/file`, { method: 'POST', body })
  if (!response.ok) {
    throw new Error('request file upload failed')
  }
}

/** Адрес для скачивания: браузер идёт через общий прокси фронтенда и передаёт куки сам. */
export const requestFileHref = (requestId: string, fileId: string) =>
  `/api/server/fulfillment/request/${requestId}/file/${fileId}`

const close = (id: string) => axiosProxy.post<ApiResponse<{ closed: boolean }>>(`/fulfillment/request/${id}/close`, {})

/** Продавец увидел ответы: со следующего захода они перестанут быть новыми. */
const markOffersSeen = () => axiosProxy.post<ApiResponse<{ seen: boolean }>>('/fulfillment/request/my/offers-seen', {})

export const api = {
  create,
  getRequest,
  edit,
  myList,
  inbox,
  inboxCount,
  offer,
  decline,
  cancelDecline,
  close,
  markOffersSeen,
  attachFile,
}
